import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Avatar,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  LinearProgress,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AccessTime as ClockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  EditCalendar as RegularizeIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  InfoOutlined as InfoIcon,
  ViewList as ViewListIcon,
  GridView as GridViewIcon,
  Place as PlaceIcon,
  Shield as PolicyIcon,
  Today as TodayIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import {
  selectAttendanceRecords,
  selectAttendanceRecordsLoading,
  selectAttendanceRecordsPagination,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";
import { fetchMyAttendanceRecords } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";
import {
  clockIn,
  clockOut,
  getTodayStatus,
  submitRegularization,
} from "services/company/attendanceManagement/attendanceRecords/attendanceRecordsService";
import { showSuccessToast, showErrorToast } from "utils/toastNotification";

const MyAttendance = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";

  // Redux Selectors
  const attendanceRecords = useSelector(selectAttendanceRecords) || [];
  const loading = useSelector(selectAttendanceRecordsLoading);
  const pagination = useSelector(selectAttendanceRecordsPagination);
  const currentUser = useSelector(selectCurrentUser);

  // Month Navigation State
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [activeTab, setActiveTab] = useState(0); // 0: Logs, 1: Calendar, 2: Policy

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Today Clock In/Out Live Status State
  const [personalStatus, setPersonalStatus] = useState(null);
  const [fetchingStatus, setFetchingStatus] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [, setTimerTick] = useState(0);

  // Regularization Modal State
  const [regularizeModalOpen, setRegularizeModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [regForm, setRegForm] = useState({
    date: "",
    checkIn: "",
    checkOut: "",
    reason: "Forgot to Clock In/Out",
    remarks: "",
  });

  // Calculate start and end date for current selected month YYYY-MM-DD
  const daysInMonthCount = currentDate.daysInMonth();
  const fromDate = currentDate.startOf("month").format("YYYY-MM-DD");
  const toDate = currentDate.endOf("month").format("YYYY-MM-DD");

  // Load My Attendance Data via Redux
  const loadMyAttendance = useCallback(() => {
    dispatch(
      fetchMyAttendanceRecords({
        from_date: fromDate,
        to_date: toDate,
        page: 1,
        limit: 100,
      }),
    );
  }, [dispatch, fromDate, toDate]);

  // Load Personal Today Punch Status (from GET /v1/api/attendance/today-status)
  const fetchPersonalTodayStatus = useCallback(async () => {
    try {
      setFetchingStatus(true);
      const res = await getTodayStatus();
      if (res?.meta?.status === true || res?.status === 200) {
        const data = res.data || res;
        setPersonalStatus(data);
      }
    } catch (err) {
      console.warn("Failed to fetch today status:", err);
    } finally {
      setFetchingStatus(false);
    }
  }, []);

  useEffect(() => {
    loadMyAttendance();
    fetchPersonalTodayStatus();
  }, [loadMyAttendance, fetchPersonalTodayStatus]);

  const activeRecord = personalStatus;
  const isCheckedIn = Boolean(
    activeRecord?.check_in && !activeRecord?.check_out,
  );
  const isCheckedOut = Boolean(
    activeRecord?.check_in && activeRecord?.check_out,
  );
  const notPunched = !activeRecord?.check_in;
  const isLeaveToday =
    Boolean(activeRecord?.is_leave) ||
    (activeRecord?.status || "").toLowerCase() === "on_leave";
  const isHolidayToday =
    Boolean(activeRecord?.is_holiday) ||
    (activeRecord?.status || "").toLowerCase() === "holiday";
  const isWeekOffToday =
    Boolean(activeRecord?.is_week_off) ||
    (activeRecord?.status || "").toLowerCase() === "week_off";

  // Live timer interval to update active check-in duration every 30 seconds
  useEffect(() => {
    if (!isCheckedIn) return;
    const interval = setInterval(() => {
      setTimerTick((t) => t + 1);
    }, 30000);
    return () => clearInterval(interval);
  }, [isCheckedIn]);

  // Format Time string
  const formatTime = (dateStr) => {
    if (!dateStr) return "--:--";
    return new Date(dateStr).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Format Shift Time string (e.g., "09:30:00" -> "09:30 AM")
  const formatShiftTime = (timeStr, defaultFormatted = "--:--") => {
    if (!timeStr) return defaultFormatted;
    const parts = String(timeStr).split(":");
    let h = parseInt(parts[0], 10);
    let m = parseInt(parts[1], 10);
    if (isNaN(h)) return timeStr;
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    const formattedH = String(h).padStart(2, "0");
    const formattedM = String(m || 0).padStart(2, "0");
    return `${formattedH}:${formattedM} ${ampm}`;
  };

  // Calculate Logged Duration
  const calculateHoursLogged = () => {
    if (!activeRecord?.check_in) return "0 hrs 0 mins";

    if (activeRecord?.working_minutes && activeRecord?.check_out) {
      const hrs = Math.floor(activeRecord.working_minutes / 60);
      const mins = activeRecord.working_minutes % 60;
      return `${hrs} hrs ${mins} mins`;
    }

    const startTime = new Date(activeRecord.check_in).getTime();
    const endTime = activeRecord.check_out
      ? new Date(activeRecord.check_out).getTime()
      : new Date().getTime();
    const diffMs = Math.max(0, endTime - startTime);
    const totalMins = Math.floor(diffMs / (1000 * 60));
    const hrs = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    return `${hrs} hrs ${mins} mins`;
  };

  // Helper to obtain GPS coordinates or fallback
  const getCoordinates = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ latitude: 0, longitude: 0 });
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
        },
        () => {
          resolve({ latitude: 0, longitude: 0 });
        },
        { timeout: 5000 },
      );
    });
  };

  // Clock In Action
  const handleCheckIn = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockIn(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(response?.meta?.message || "Checked in successfully!");
        await fetchPersonalTodayStatus();
        loadMyAttendance();
      } else {
        const errorMsg = response?.meta?.message || "Check-in failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-in error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-in failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Clock Out Action
  const handleCheckOut = async () => {
    setActionLoading(true);
    try {
      const coords = await getCoordinates();
      const response = await clockOut(coords);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(
          response?.meta?.message || "Checked out successfully!",
        );
        await fetchPersonalTodayStatus();
        loadMyAttendance();
      } else {
        const errorMsg = response?.meta?.message || "Check-out failed";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Check-out error:", err);
      showErrorToast(err?.meta?.message || err?.message || "Check-out failed");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Month Navigation
  const handlePrevMonth = () => {
    setCurrentDate((prev) => prev.subtract(1, "month"));
    setPage(0);
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => prev.add(1, "month"));
    setPage(0);
  };

  const handleCurrentMonth = () => {
    setCurrentDate(dayjs());
    setPage(0);
  };

  // Open Regularization Modal
  const handleOpenRegularizeModal = (record = null) => {
    if (record) {
      setSelectedRecord(record);
      setRegForm({
        date: record.record_date || dayjs().format("YYYY-MM-DD"),
        checkIn: record.check_in
          ? dayjs(record.check_in).format("HH:mm")
          : "09:30",
        checkOut: record.check_out
          ? dayjs(record.check_out).format("HH:mm")
          : "18:30",
        reason: "Forgot to Clock In/Out",
        remarks: "",
      });
    } else {
      setSelectedRecord(null);
      setRegForm({
        date: dayjs().format("YYYY-MM-DD"),
        checkIn: "09:30",
        checkOut: "18:30",
        reason: "Forgot to Clock In/Out",
        remarks: "",
      });
    }
    setRegularizeModalOpen(true);
  };

  const handleCloseRegularizeModal = () => {
    setRegularizeModalOpen(false);
  };

  const handleSubmitRegularization = async (e) => {
    e.preventDefault();
    setActionLoading(true);

    try {
      const payload = {
        attendance_date: regForm.date,
        requested_check_in: regForm.checkIn,
        requested_check_out: regForm.checkOut,
        reason: regForm.reason,
      };

      const response = await submitRegularization(payload);

      if (response?.meta?.status === true || response?.status === 200) {
        showSuccessToast(
          response?.meta?.message ||
            "Attendance Regularization Request submitted successfully! Manager approval pending.",
        );
        setRegularizeModalOpen(false);
        loadMyAttendance();
      } else {
        const errorMsg =
          response?.meta?.message || "Failed to submit regularization request";
        showErrorToast(errorMsg);
      }
    } catch (err) {
      console.error("Regularization error:", err);
      showErrorToast(
        err?.meta?.message ||
          err?.message ||
          "Failed to submit regularization request",
      );
    } finally {
      setActionLoading(false);
    }
  };

  // Monthly KPI Calculation
  // Monthly KPI Calculation
  const calculateMonthStats = () => {
    let totalWorkingMinutes = 0;
    let presentCount = 0;
    let absentCount = 0;
    let lateCount = 0;
    let holidayCount = 0; // includes H + HP
    let weekOffCount = 0; // includes WO + WoP
    let leaveCount = 0;
    let totalLateMinutes = 0;
    let totalOvertimeMinutes = 0;

    // Extra work days (worked on non-working days / on leave)
    let holidayPresentCount = 0; // HP
    let weekOffPresentCount = 0; // WoP
    let leavePresentCount = 0; // LP

    attendanceRecords.forEach((rec) => {
      const status = (rec.status || rec.attendance_status || "").toLowerCase();

      if (rec.working_minutes) {
        totalWorkingMinutes += Number(rec.working_minutes);
      }
      if (rec.overtime_minutes) {
        totalOvertimeMinutes += Number(rec.overtime_minutes);
      }
      if (rec.is_late || rec.late_minutes > 0) {
        lateCount += 1;
        totalLateMinutes += Number(rec.late_minutes || 0);
      }

      const isHoliday =
        status === "holiday" ||
        status === "holiday_present" ||
        Boolean(rec.is_holiday);
      const isHolidayPresent =
        status === "holiday_present" ||
        (rec.is_holiday && Boolean(rec.check_in));
      const isWeekOff =
        status === "week_off" ||
        status === "weekend" ||
        status === "week_off_present" ||
        Boolean(rec.is_week_off);
      const isWeekOffPresent =
        status === "week_off_present" ||
        ((Boolean(rec.is_week_off) ||
          status === "week_off" ||
          status === "weekend") &&
          Boolean(rec.check_in));
      const isLeave =
        status === "on_leave" ||
        status === "leave" ||
        status === "leave_present" ||
        Boolean(rec.is_leave);
      const isLeavePresent =
        status === "leave_present" ||
        (Boolean(rec.is_leave) && Boolean(rec.check_in));

      // Counters (each record falls into ONE bucket)
      if (isHoliday) {
        holidayCount += 1;
        if (isHolidayPresent) holidayPresentCount += 1;
      } else if (isWeekOff) {
        weekOffCount += 1;
        if (isWeekOffPresent) weekOffPresentCount += 1;
      } else if (isLeave) {
        leaveCount += 1;
        if (isLeavePresent) leavePresentCount += 1;
      } else if (rec.check_in || status === "present" || status === "late") {
        presentCount += 1;
      } else if (status === "absent" && !rec.check_in) {
        absentCount += 1;
      }
    });

    const hours = Math.floor(totalWorkingMinutes / 60);
    const mins = totalWorkingMinutes % 60;
    const avgDailyMins =
      presentCount > 0 ? Math.round(totalWorkingMinutes / presentCount) : 0;
    const avgHours = Math.floor(avgDailyMins / 60);
    const avgMins = avgDailyMins % 60;

    // Working Days = Total days in month − Holidays (H + HP) − Week Offs (WO + WoP)
    const totalDaysInMonth = daysInMonthCount;
    const workingDaysCount = totalDaysInMonth - holidayCount - weekOffCount;

    // Extra Work Days = HP + WoP + LP
    const extraWorkDaysCount =
      holidayPresentCount + weekOffPresentCount + leavePresentCount;

    return {
      totalHoursStr: `${hours}h ${mins}m`,
      avgHoursStr: `${avgHours}h ${avgMins}m / day`,
      presentCount,
      absentCount,
      lateCount,
      holidayCount,
      weekOffCount,
      leaveCount,
      totalLateMinutes,
      totalOvertimeMinutes,
      workingDaysCount,
      extraWorkDaysCount,
      holidayPresentCount,
      weekOffPresentCount,
      leavePresentCount,
      totalDaysInMonth,
    };
  };

  const stats = calculateMonthStats();

  // Helper to format minutes to string e.g. 510 -> 8h 30m
  const formatMins = (mins) => {
    if (!mins || mins <= 0) return "0h 00m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m < 10 ? "0" : ""}${m}m`;
  };

  // Status Chip Rendering
  const renderStatusChip = (rec) => {
    const status = (
      rec.status ||
      rec.attendance_status ||
      "absent"
    ).toLowerCase();
    const isWeekOff =
      Boolean(rec.is_week_off) || status === "week_off" || status === "weekend";

    if (
      rec.check_in ||
      status === "present" ||
      status === "holiday_present" ||
      status === "week_off_present" ||
      status === "leave_present"
    ) {
      if (status === "holiday_present" || (rec.is_holiday && rec.check_in)) {
        return (
          <Chip
            label="HOLIDAY PRESENT [HP]"
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(16, 185, 129, 0.25)" : "#d1fae5",
              color: isDark ? "#6ee7b7" : "#065f46",
              fontWeight: 700,
              fontSize: "0.72rem",
              borderRadius: "8px",
            }}
          />
        );
      }
      if (status === "week_off_present" || (isWeekOff && rec.check_in)) {
        return (
          <Chip
            label="WEEK OFF PRESENT [WoP]"
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(16, 185, 129, 0.25)" : "#d1fae5",
              color: isDark ? "#6ee7b7" : "#065f46",
              fontWeight: 700,
              fontSize: "0.72rem",
              borderRadius: "8px",
            }}
          />
        );
      }
      if (status === "leave_present" || (rec.is_leave && rec.check_in)) {
        return (
          <Chip
            label="LEAVE PRESENT [LP]"
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(16, 185, 129, 0.25)" : "#d1fae5",
              color: isDark ? "#6ee7b7" : "#065f46",
              fontWeight: 700,
              fontSize: "0.72rem",
              borderRadius: "8px",
            }}
          />
        );
      }
      if (rec.is_late || status === "late") {
        return (
          <Chip
            label="LATE PRESENT [L]"
            size="small"
            sx={{
              bgcolor: isDark ? "rgba(245, 158, 11, 0.2)" : "#fef3c7",
              color: isDark ? "#fde68a" : "#92400e",
              fontWeight: 700,
              fontSize: "0.72rem",
              borderRadius: "8px",
            }}
          />
        );
      }
      return (
        <Chip
          label="PRESENT [P]"
          size="small"
          sx={{
            bgcolor: isDark ? "rgba(16, 185, 129, 0.2)" : "#d1fae5",
            color: isDark ? "#6ee7b7" : "#065f46",
            fontWeight: 700,
            fontSize: "0.72rem",
            borderRadius: "8px",
          }}
        />
      );
    } else if (status === "holiday" || rec.is_holiday) {
      return (
        <Chip
          label={
            rec.holiday_name
              ? `HOLIDAY [H]: ${rec.holiday_name.toUpperCase()}`
              : "HOLIDAY [H]"
          }
          size="small"
          sx={{
            bgcolor: isDark ? "rgba(59, 130, 246, 0.2)" : "#dbeafe",
            color: isDark ? "#93c5fd" : "#1e40af",
            fontWeight: 700,
            fontSize: "0.72rem",
            borderRadius: "8px",
          }}
        />
      );
    } else if (status === "on_leave" || status === "leave" || rec.is_leave) {
      const code = rec.short_status || "L";
      const leaveLabel = rec.leave_name
        ? `ON LEAVE [${code}]: ${rec.leave_name.toUpperCase()}`
        : `ON LEAVE [${code}]`;
      return (
        <Chip
          label={leaveLabel}
          size="small"
          sx={{
            bgcolor: isDark ? "rgba(168, 85, 247, 0.2)" : "#f3e8ff",
            color: isDark ? "#d8b4fe" : "#6b21a8",
            fontWeight: 700,
            fontSize: "0.72rem",
            borderRadius: "8px",
          }}
        />
      );
    } else if (isWeekOff) {
      return (
        <Chip
          label={
            rec.remarks && rec.remarks.includes("Off")
              ? `${rec.remarks.toUpperCase()} [WO]`
              : "WEEK OFF [WO]"
          }
          size="small"
          sx={{
            bgcolor: isDark ? "rgba(99, 102, 241, 0.2)" : "#e0e7ff",
            color: isDark ? "#a5b4fc" : "#3730a3",
            fontWeight: 600,
            fontSize: "0.72rem",
            borderRadius: "8px",
          }}
        />
      );
    } else {
      return (
        <Chip
          label="ABSENT [A]"
          size="small"
          sx={{
            bgcolor: isDark ? "rgba(239, 68, 68, 0.2)" : "#fee2e2",
            color: isDark ? "#fca5a5" : "#991b1b",
            fontWeight: 700,
            fontSize: "0.72rem",
            borderRadius: "8px",
          }}
        />
      );
    }
  };

  // Paginated Data slice
  const paginatedRecords = attendanceRecords.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* ── HEADER BANNER + TODAY'S PUNCH STATUS (PERFECT 50% / 50% SPLIT) ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: 1,
            borderColor: "divider",
            backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
            overflow: "hidden",
          }}
        >
          <Grid container alignItems="stretch">
            {/* Left Side: Exactly 50% Width - White Background */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                width: "50%",
                p: { xs: 3, md: 3.5 },
                backgroundColor: isDark ? "transparent" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 2.5,
              }}
            >
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary }}
                  >
                    My Attendance
                  </Typography>
                  <Chip
                    label="Employee Portal"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      borderRadius: "8px",
                    }}
                  />
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Track your daily work log, working hours, and regularization
                  requests.
                </Typography>
              </Box>

              {/* Month Selector Controls & Actions */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexWrap="wrap"
              >
                <Paper
                  elevation={0}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f8fafc",
                    borderRadius: "10px",
                    p: 0.5,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <IconButton size="small" onClick={handlePrevMonth}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography
                    variant="body2"
                    sx={{
                      px: 2,
                      fontWeight: 700,
                      minWidth: 130,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "center",
                    }}
                  >
                    <CalendarIcon color="primary" sx={{ fontSize: 16 }} />
                    {currentDate.format("MMMM YYYY")}
                  </Typography>
                  <IconButton size="small" onClick={handleNextMonth}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Paper>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCurrentMonth}
                  startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "10px",
                  }}
                >
                  This Month
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RegularizeIcon />}
                  onClick={() => handleOpenRegularizeModal()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                    boxShadow: "none",
                  }}
                >
                  Request Regularization
                </Button>
              </Stack>
            </Grid>

            {/* Right Side: Exactly 50% Width - Yellow Background (Clean 2-Row Punch Display) */}
            <Grid
              item
              // xs={12}
              // md={6}
              sx={{
                width: "50%",
                p: { xs: 3, md: 3.5 },
                backgroundColor: isDark
                  ? "rgba(245, 158, 11, 0.12)"
                  : "#fffbeb",
                borderLeft: {
                  md: `1px solid ${
                    isDark ? "rgba(245, 158, 11, 0.25)" : "#fde68a"
                  }`,
                },
                borderTop: {
                  xs: `1px solid ${
                    isDark ? "rgba(245, 158, 11, 0.25)" : "#fde68a"
                  }`,
                  md: "none",
                },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              {/* Header Title & Status Chip */}
              <Stack
                direction="row"
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: isDark ? "rgba(245, 158, 11, 0.25)" : "#fef3c7",
                      color: "#d97706",
                      border: `1px solid ${
                        isDark ? "rgba(245, 158, 11, 0.4)" : "#fde68a"
                      }`,
                    }}
                  >
                    <ClockIcon fontSize="small" />
                  </Avatar>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      color: isDark ? "#fbbf24" : "#92400e",
                    }}
                  >
                    My Attendance Status Today
                    {/* Row 2: Hours Logged Today */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      // sx={{ pt: 1, borderTop: `1px dashed ${isDark ? "rgba(245, 158, 11, 0.4)" : "#fde68a"}` }}
                    >
                      {/* <Typography variant="body2" sx={{ fontWeight: 700, color: isDark ? "#fef08a" : "#78350f" }}>
                      Hours Logged Today:
                    </Typography> */}
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          color: isDark ? "#fbbf24" : "#b45309",
                          fontFamily: "monospace",
                        }}
                      >
                        {calculateHoursLogged()}
                      </Typography>
                    </Stack>
                  </Typography>
                </Stack>

                <Chip
                  label={
                    isCheckedIn
                      ? "Checked In"
                      : isCheckedOut
                      ? "Shift Ended"
                      : isLeaveToday
                      ? activeRecord?.leave_name
                        ? `On Leave (${activeRecord.leave_name})`
                        : "On Leave"
                      : isHolidayToday
                      ? activeRecord?.holiday_name
                        ? `Holiday (${activeRecord.holiday_name})`
                        : "Holiday"
                      : isWeekOffToday
                      ? "Week Off"
                      : "Not Checked In"
                  }
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderRadius: "8px",
                    backgroundColor: isCheckedIn
                      ? "#10b981"
                      : isCheckedOut
                      ? "#6366f1"
                      : isLeaveToday
                      ? "#3b82f6"
                      : isHolidayToday
                      ? "#0284c7"
                      : isWeekOffToday
                      ? "#64748b"
                      : "#d97706",
                    color: "#ffffff",
                  }}
                />
              </Stack>

              {/* Punch Timings & Total Hours Display (2 Clean Rows) */}
              {fetchingStatus && !activeRecord?.check_in ? (
                <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
                  <CircularProgress size={20} sx={{ color: "#d97706" }} />
                </Box>
              ) : (
                <Stack spacing={1.5} sx={{ py: 0.5 }}>
                  {/* Row 1: Check In & Check Out */}
                  <Stack
                    direction="row"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LoginIcon style={{ color: "#10b981", fontSize: 18 }} />
                      {/* <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? "#fef08a" : "#78350f" }}>
                        Check In:
                      </Typography> */}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 800,
                          color: isDark ? "#fff" : "#451a03",
                          fontFamily: "monospace",
                        }}
                      >
                        {formatTime(activeRecord?.check_in)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <LogoutIcon
                        style={{
                          color: activeRecord?.check_out
                            ? "#ef4444"
                            : "#94a3b8",
                          fontSize: 18,
                        }}
                      />
                      {/* <Typography variant="body2" sx={{ fontWeight: 600, color: isDark ? "#fef08a" : "#78350f" }}>
                        Check Out:
                      </Typography> */}
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 800,
                          color: isDark ? "#fff" : "#451a03",
                          fontFamily: "monospace",
                        }}
                      >
                        {formatTime(activeRecord?.check_out)}
                      </Typography>
                    </Stack>
                    {/* Action Button */}
                    <Box>
                      {notPunched && (
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={actionLoading}
                          onClick={handleCheckIn}
                          startIcon={
                            actionLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <LoginIcon />
                            )
                          }
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 800,
                            py: 1,
                            background:
                              "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                            boxShadow: "0 4px 14px rgba(16, 185, 129, 0.35)",
                            color: "#ffffff",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #059669 0%, #047857 100%)",
                            },
                          }}
                        >
                          {actionLoading ? "Punching In..." : "Check In Now"}
                        </Button>
                      )}

                      {isCheckedIn && (
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={actionLoading}
                          onClick={handleCheckOut}
                          startIcon={
                            actionLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <LogoutIcon />
                            )
                          }
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 800,
                            py: 1,
                            background:
                              "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)",
                            color: "#ffffff",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                            },
                          }}
                        >
                          {actionLoading ? "Punching Out..." : "Check Out Now"}
                        </Button>
                      )}

                      {isCheckedOut && (
                        <Button
                          variant="outlined"
                          fullWidth
                          disabled
                          startIcon={<CheckCircleIcon color="primary" />}
                          sx={{
                            borderRadius: "10px",
                            textTransform: "none",
                            fontWeight: 700,
                            py: 0.8,
                            borderColor: isDark
                              ? "rgba(99, 102, 241, 0.3)"
                              : "#c7d2fe",
                            color: "primary.main",
                          }}
                        >
                          Shift Completed Today
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </Stack>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* ── KEKA SUMMARY METRIC CARDS GRID (EQUAL HEIGHT & ICON SPACING) ── */}
        <Grid container spacing={2.5} alignItems="stretch">
          {/* Total Working Hours */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "16px",
                border: 1,
                borderColor: "divider",
                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                  }}
                >
                  Total Work Hours
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    width: 36,
                    height: 36,
                    ml: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <ClockIcon fontSize="small" />
                </Avatar>
              </Stack>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.totalHoursStr}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <TrendingUpIcon color="success" sx={{ fontSize: 16 }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    Avg {stats.avgHoursStr}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Grid>

          {/* Present Days */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "16px",
                border: 1,
                borderColor: "divider",
                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ fontWeight: 700, mt: 1, display: "block" }}
                >
                  {stats.presentCount > 0 && stats.workingDaysCount > 0
                    ? `${Math.round(
                        (stats.presentCount / stats.workingDaysCount) * 100,
                      )}% Attendance Rate`
                    : "0% Attendance"}
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    width: 36,
                    height: 36,
                    ml: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </Avatar>
              </Stack>
              <Box>
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.presentCount}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    / {stats.workingDaysCount} Days
                  </Typography>
                </Stack>
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ fontWeight: 700, mt: 1, display: "block" }}
                >
                  {stats.presentCount > 0
                    ? `${Math.round(
                        (stats.presentCount / stats.workingDaysCount) * 100,
                      )}% Attendance Rate`
                    : "0% Attendance"}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Absent / Unlogged */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "16px",
                border: 1,
                borderColor: "divider",
                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                  }}
                >
                  Absent / Unlogged
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    width: 36,
                    height: 36,
                    ml: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <CancelIcon fontSize="small" />
                </Avatar>
              </Stack>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {stats.absentCount}
                </Typography>
                <Typography
                  variant="caption"
                  color="error.main"
                  sx={{ fontWeight: 600, mt: 1, display: "block" }}
                >
                  {stats.absentCount > 0
                    ? "Requires Regularization"
                    : "No Unexcused Absences"}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Extra Work Days (HP / WoP / LP) */}
          <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "16px",
                border: 1,
                borderColor: "divider",
                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.02em",
                  }}
                >
                  Extra Work Days
                </Typography>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    width: 36,
                    height: 36,
                    ml: 1.5,
                    flexShrink: 0,
                  }}
                >
                  <TrendingUpIcon fontSize="small" />
                </Avatar>
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={1}
              >
                {/* Left: big number */}
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, lineHeight: 1 }}
                >
                  {stats.extraWorkDaysCount}
                </Typography>

                {/* Right: breakdown — only shows lines with value > 0 */}
                {stats.extraWorkDaysCount > 0 && (
                  <Stack spacing={0.25} sx={{ textAlign: "right" }}>
                    {stats.holidayPresentCount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "#94a3b8" : "#64748b",
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          lineHeight: 1.2,
                        }}
                      >
                        Holiday Present: {stats.holidayPresentCount}
                      </Typography>
                    )}
                    {stats.weekOffPresentCount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "#94a3b8" : "#64748b",
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          lineHeight: 1.2,
                        }}
                      >
                        Week Off Present: {stats.weekOffPresentCount}
                      </Typography>
                    )}
                    {stats.leavePresentCount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDark ? "#94a3b8" : "#64748b",
                          fontWeight: 600,
                          fontSize: "0.68rem",
                          lineHeight: 1.2,
                        }}
                      >
                        Leave Present: {stats.leavePresentCount}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Late / Overtime */}
          {/* <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                width: "100%",
                borderRadius: "16px",
                border: 1,
                borderColor: "divider",
                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.02em" }}>
                  Late / Overtime
                </Typography>
                <Avatar sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: theme.palette.warning.main, width: 36, height: 36, ml: 1.5, flexShrink: 0 }}>
                  <WarningIcon fontSize="small" />
                </Avatar>
              </Stack>
              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.warning.dark }}>
                      {stats.lateCount} Days
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.totalLateMinutes}m Late
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {formatMins(stats.totalOvertimeMinutes)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Overtime
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>
          </Grid> */}
        </Grid>

        {/* ── MAIN CONTENT PAPER WITH TABS & MUI TABLE ── */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: 1,
            borderColor: "divider",
            backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#ffffff",
            overflow: "hidden",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, pt: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newVal) => setActiveTab(newVal)}
              textColor="primary"
              indicatorColor="primary"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  minHeight: 48,
                },
              }}
            >
              <Tab
                icon={<ViewListIcon fontSize="small" />}
                iconPosition="start"
                label="Logs & Shift Timeline"
              />
              <Tab
                icon={<GridViewIcon fontSize="small" />}
                iconPosition="start"
                label="Monthly Calendar"
              />
              <Tab
                icon={<PolicyIcon fontSize="small" />}
                iconPosition="start"
                label="Shift & Policy Insights"
              />
            </Tabs>
          </Box>

          {/* LOADING STATE */}
          {loading && (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CircularProgress />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, fontWeight: 500 }}
              >
                Loading attendance records...
              </Typography>
            </Box>
          )}

          {/* TAB 0: LOGS TABLE */}
          {!loading && activeTab === 0 && (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: isDark
                          ? "rgba(255, 255, 255, 0.05)"
                          : "#f8fafc",
                      }}
                    >
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Date & Day
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Shift Timings
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Clock In
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Clock Out
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          minWidth: 200,
                        }}
                      >
                        Shift Timeline Bar
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                        }}
                      >
                        Total Hours
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          textAlign: "center",
                        }}
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRecords.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          align="center"
                          sx={{ py: 6, color: "text.secondary" }}
                        >
                          No attendance logs found for this period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedRecords.map((rec, index) => {
                        const dateObj = dayjs(rec.record_date);
                        const isToday = dateObj.isSame(dayjs(), "day");
                        const formattedCheckIn = rec.check_in
                          ? dayjs(rec.check_in).format("hh:mm A")
                          : "--:--";
                        const formattedCheckOut = rec.check_out
                          ? dayjs(rec.check_out).format("hh:mm A")
                          : "--:--";
                        const workMins = rec.working_minutes || 0;
                        const shiftMins = rec.shift_duration_minutes || 540;
                        const progressPercent = Math.min(
                          100,
                          Math.round((workMins / shiftMins) * 100),
                        );
                        const shiftStartTimeFormatted = formatShiftTime(
                          rec.shift_start_time,
                          "09:30 AM",
                        );
                        const shiftEndTimeFormatted = formatShiftTime(
                          rec.shift_end_time,
                          "06:30 PM",
                        );

                        return (
                          <TableRow
                            key={rec.id || index}
                            hover
                            sx={{
                              bgcolor: isToday
                                ? alpha(theme.palette.primary.main, 0.04)
                                : "inherit",
                            }}
                          >
                            {/* Date & Day */}
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <Paper
                                  elevation={0}
                                  sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: "10px",
                                    bgcolor: isDark
                                      ? "rgba(255, 255, 255, 0.05)"
                                      : "#f8fafc",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: `1px solid ${theme.palette.divider}`,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 700, lineHeight: 1 }}
                                  >
                                    {dateObj.format("DD")}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                      fontSize: "0.65rem",
                                      textTransform: "uppercase",
                                    }}
                                  >
                                    {dateObj.format("ddd")}
                                  </Typography>
                                </Paper>
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {dateObj.format("MMM DD, YYYY")}
                                  </Typography>
                                  {isToday && (
                                    <Chip
                                      label="TODAY"
                                      size="small"
                                      color="primary"
                                      sx={{
                                        height: 16,
                                        fontSize: "0.6rem",
                                        fontWeight: 700,
                                        borderRadius: "4px",
                                      }}
                                    />
                                  )}
                                </Box>
                              </Stack>
                            </TableCell>

                            {/* Status */}
                            <TableCell>{renderStatusChip(rec)}</TableCell>

                            {/* Shift */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, fontSize: "0.825rem" }}
                              >
                                {rec.shift_name || "General Shift"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {shiftStartTimeFormatted} -{" "}
                                {shiftEndTimeFormatted}
                              </Typography>
                            </TableCell>

                            {/* Clock In */}
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {formattedCheckIn}
                                </Typography>
                                {rec.check_in_method && (
                                  <Tooltip
                                    title={`Method: ${rec.check_in_method}`}
                                  >
                                    <PlaceIcon
                                      sx={{
                                        fontSize: 14,
                                        color: "text.disabled",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                              {rec.is_late === 1 && (
                                <Typography
                                  variant="caption"
                                  color="warning.dark"
                                  sx={{ fontWeight: 700, display: "block" }}
                                >
                                  +{rec.late_minutes}m Late
                                </Typography>
                              )}
                            </TableCell>

                            {/* Clock Out */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {formattedCheckOut}
                              </Typography>
                            </TableCell>

                            {/* Shift Timeline Bar */}
                            <TableCell>
                              <Box sx={{ width: "100%" }}>
                                <Stack
                                  direction="row"
                                  sx={{
                                    mb: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {shiftStartTimeFormatted}
                                  </Typography>
                                  {/* <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                                    {workMins > 0 ? formatMins(workMins) : "0h"}
                                  </Typography> */}
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {shiftEndTimeFormatted}
                                  </Typography>
                                </Stack>
                                <LinearProgress
                                  variant="determinate"
                                  value={progressPercent}
                                  color={rec.is_late ? "warning" : "success"}
                                  sx={{ height: 6, borderRadius: 3 }}
                                />
                              </Box>
                            </TableCell>

                            {/* Total Time */}
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {formatMins(workMins)}
                              </Typography>
                            </TableCell>

                            {/* Action */}
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleOpenRegularizeModal(rec)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 600,
                                  borderRadius: "8px",
                                }}
                              >
                                Request to Regularize
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination Controls */}
              <TablePagination
                rowsPerPageOptions={[10, 20, 50]}
                component="div"
                count={attendanceRecords.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}

          {/* TAB 1: CALENDAR VIEW */}
          {!loading && activeTab === 1 && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {attendanceRecords.map((rec, index) => {
                  const dateObj = dayjs(rec.record_date);
                  const isWeekOff =
                    Boolean(rec.is_week_off) ||
                    (rec.status || "").toLowerCase() === "week_off";
                  const isHoliday =
                    Boolean(rec.is_holiday) ||
                    (rec.status || "").toLowerCase() === "holiday";
                  const isLeave =
                    Boolean(rec.is_leave) ||
                    (rec.status || "").toLowerCase() === "on_leave" ||
                    (rec.status || "").toLowerCase() === "leave";

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={3}
                      lg={2.4}
                      key={rec.id || index}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          borderRadius: "12px",
                          border: `1px solid ${theme.palette.divider}`,
                          bgcolor: rec.check_in
                            ? isDark
                              ? "rgba(16, 185, 129, 0.05)"
                              : "#f0fdf4"
                            : isHoliday
                            ? isDark
                              ? "rgba(59, 130, 246, 0.05)"
                              : "#eff6ff"
                            : isLeave
                            ? isDark
                              ? "rgba(59, 130, 246, 0.1)"
                              : "#eff6ff"
                            : isWeekOff
                            ? isDark
                              ? "rgba(255, 255, 255, 0.02)"
                              : "#f8fafc"
                            : isDark
                            ? "rgba(239, 68, 68, 0.05)"
                            : "#fef2f2",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {dateObj.format("DD MMM")}{" "}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                            >
                              ({dateObj.format("ddd")})
                            </Typography>
                          </Typography>
                          {renderStatusChip(rec)}
                        </Stack>

                        <Stack spacing={0.75} sx={{ my: 1 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            In:{" "}
                            <strong>
                              {rec.check_in
                                ? dayjs(rec.check_in).format("hh:mm A")
                                : "--"}
                            </strong>
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Out:{" "}
                            <strong>
                              {rec.check_out
                                ? dayjs(rec.check_out).format("hh:mm A")
                                : "--"}
                            </strong>
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={2.5}
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{
                            pt: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {formatMins(rec.working_minutes)}
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => handleOpenRegularizeModal(rec)}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.75rem",
                              p: 0,
                              minWidth: "auto",
                              fontWeight: 600,
                            }}
                          >
                            Regularize
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* TAB 2: SHIFT & POLICY INSIGHTS */}
          {!loading &&
            activeTab === 2 &&
            (() => {
              const activeShiftName =
                attendanceRecords[0]?.shift_name ||
                personalStatus?.shift_name ||
                "General Shift";
              const activeShiftStart =
                attendanceRecords[0]?.shift_start_time ||
                personalStatus?.shift_start_time ||
                "09:30:00";
              const activeShiftEnd =
                attendanceRecords[0]?.shift_end_time ||
                personalStatus?.shift_end_time ||
                "18:30:00";
              const activeShiftMins =
                attendanceRecords[0]?.shift_duration_minutes ||
                personalStatus?.shift_duration_minutes ||
                540;

              return (
                <Box sx={{ p: 3 }}>
                  <Alert
                    icon={<InfoIcon />}
                    severity="info"
                    sx={{ mb: 3, borderRadius: "12px" }}
                  >
                    Attendance rules and policies are set by company HR to track
                    shift timings, grace periods, and regularization quotas.
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "14px",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 2 }}
                        >
                          {activeShiftName} Details
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Shift Start Time:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatShiftTime(activeShiftStart, "09:30 AM")}
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Shift End Time:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatShiftTime(activeShiftEnd, "06:30 PM")}
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row"spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Total Shift Hours:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {formatMins(activeShiftMins)}
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Grace Period:
                            </Typography>
                            <Typography
                              variant="body2"
                              color="success.main"
                              sx={{ fontWeight: 700 }}
                            >
                              15 Minutes
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: "14px",
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 2 }}
                        >
                          Regularization Guidelines
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Monthly Regularization Quota:
                            </Typography>
                            <Typography
                              variant="body2"
                              color="primary"
                              sx={{ fontWeight: 700 }}
                            >
                              No Limit
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Submission Timeframe:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              Within 7 days of punch date
                            </Typography>
                          </Stack>
                          <Divider />
                          <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">
                              Approval Workflow:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              Reporting Manager / HR
                            </Typography>
                          </Stack>
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              );
            })()}
        </Paper>

        {/* ── REGULARIZATION MODAL DIALOG ── */}
        <Dialog
          open={regularizeModalOpen}
          onClose={handleCloseRegularizeModal}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              pb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <RegularizeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Attendance Regularization Request
              </Typography>
            </Stack>
            <IconButton size="small" onClick={handleCloseRegularizeModal}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <form onSubmit={handleSubmitRegularization}>
            <DialogContent sx={{ py: 2 }}>
              <Stack spacing={2.5}>
                <Alert severity="info" sx={{ borderRadius: "10px" }}>
                  Request correction for missing punch times or late check-in.
                  Your reporting manager will be notified for approval.
                </Alert>

                <TextField
                  label="Target Date"
                  type="date"
                  fullWidth
                  value={regForm.date}
                  onChange={(e) =>
                    setRegForm({ ...regForm, date: e.target.value })
                  }
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />

                <Grid container spacing={2} sx={{ width: "100%" }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Check-In"
                      type="time"
                      fullWidth
                      value={regForm.checkIn}
                      onChange={(e) =>
                        setRegForm({ ...regForm, checkIn: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Check-Out"
                      type="time"
                      fullWidth
                      value={regForm.checkOut}
                      onChange={(e) =>
                        setRegForm({ ...regForm, checkOut: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      size="small"
                    />
                  </Grid>
                </Grid>

                <TextField
                  label="Reason for Regularization"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Provide context for your manager..."
                  value={regForm.reason}
                  onChange={(e) =>
                    setRegForm({ ...regForm, reason: e.target.value })
                  }
                  size="small"
                />
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{ px: 3, pb: 2, borderTop: 1, borderColor: "divider", pt: 2 }}
            >
              <Button
                onClick={handleCloseRegularizeModal}
                disabled={actionLoading}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={actionLoading}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 3,
                }}
              >
                {actionLoading ? (
                  <CircularProgress size={20} sx={{ color: "#ffffff" }} />
                ) : (
                  "Submit Request"
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MyAttendance;
